<template>
  <div class="sm:w-full md:w4/5 lg:w-3/5 mx-auto md:p-6">
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
        <h2 class="text-xl font-semibold text-white">Add New Gift</h2>
        <p class="text-red-100 text-sm mt-1">Add a gift to your present planning list...</p>
      </div>
      <!-- Form Content -->
      <div class="p-6 space-y-6">
        <!-- Gift Details Section -->
        <div class="space-y-4">
<!-- Gift Name Field -->
          <div>
            <label for="addGiftName" class="block text-sm font-medium text-gray-700 mb-2">
              Gift Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.name"
              placeholder="A pot plant, a new pair of shoes, a new phone, etc."
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white"
              id="addGiftName"
              :class="{ 'border-red-500 bg-red-50': formError.includes('name') }"
              maxlength="100"
>
          </div>

          <!-- URL Field -->
          <div>
            <label for="addGiftURL" class="block text-sm font-medium text-gray-700 mb-2">
              URL
            </label>
            <input
              v-model="form.url"
              type="url"
              placeholder="https://www.amazon.com.au/dp/abcd1234/"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white"
              id="addGiftURL"
              maxlength="2048"
>
            <p class="text-xs text-gray-500 mt-1">Optional: Link to where this gift can be purchased</p>
          </div>

          <!-- Description Field -->
          <div>
            <label for="addGiftDescription" class="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              v-model="form.description"
              placeholder="Size: XL, Xbox format, 'he already has the brown one', etc."
              rows="3"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white resize-none"
              id="addGiftDescription"
              maxlength="500"
></textarea>
          </div>

          <!-- Price Field -->
          <div>
            <label for="addGiftPrice" class="block text-sm font-medium text-gray-700 mb-2">
              Price
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span class="text-gray-500 text-lg">$</span>
              </div>
              <input 
                v-model="form.price" 
                type="number" 
                step="0.01" 
                placeholder="15.00"
                class="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 bg-gray-50 hover:bg-white"
                id="addGiftPrice"
>
            </div>
            <p class="text-xs text-gray-500 mt-1">Optional: Estimated or actual price</p>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="formError" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Error</h3>
              <p class="text-sm text-red-700 mt-1">{{ formError }}</p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button 
            @click="$router.go(-1)"
            type="button"
            class="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors duration-200"
>
            Cancel
          </button>
          <button 
            @click="createGift" 
            type="button"
            class="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
>
            Add Gift
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from 'aws-amplify';

import { createGift } from '../graphql/mutations';

export default {
  name: 'GiftAdd',
  data() {
    return {
      form: {
        name: '',
        description: '',
        price: '',
        url: null
      },
      formError: ''
    }
  },
  methods: {
    async createGift() {
      var image = ''

      var { name, description, price, url } = this.form
      if (!price) { price = 0.00 };
      if (!name) {
        this.formError = 'You need to at least set a name for the gift.'
        return
      } else {
        this.formError = ''
      };

      const gift = { name, description, price, url, image };
      try {
        const response = await API.graphql({ query: createGift, variables: { input: gift }, });

        this.$router.go(-1)
      } catch (error) {
        console.log('Error creating gift...', error)
      }
    },
  }
}
</script>